import { Product } from '@/lib/Apis';

export interface ProductFilters {
  search?: string;
  category?: string;
  subCategory?: string;
  inStock?: boolean;
  tags?: string[];
  newArrivals?: boolean;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
}

/**
 * Filters products based on price range.
 * A product passes if ANY variant's price falls within [minPrice, maxPrice],
 * regardless of stock. Stock filtering is handled separately by the inStock toggle.
 */
export function filterProductsByPriceRange(
  products: Product[],
  minPrice?: number,
  maxPrice?: number
): Product[] {
  if (minPrice === undefined && maxPrice === undefined) {
    return products;
  }

  const min = minPrice ?? 0;
  const max = maxPrice ?? Infinity;

  const result = products.filter((product) => {
    if (!product.variants || product.variants.length === 0) {
      const price = (product as any).finalPrice ?? (product as any).price ?? 0;
      const passes = price >= min && price <= max;
      return passes;
    }

    let passes = false;
    const lines = product.variants.map((v: any) => {
      const price = v.finalPrice ?? v.price ?? 0;
      const inRange = price >= min && price <= max;
      const stock = v.inventoryCount ?? 0;
      if (inRange) passes = true;
      return `      ${inRange ? '→' : ' '} "${v.name || v._id}" price=$${price} stock=${stock}${inRange ? ' [IN RANGE]' : ''}`;
    });


    return passes;
  });

  return result;
}

/**
 * Finds the best in-stock variant within the price range to pre-select.
 * Returns null if no in-stock variant falls in range — the ProductCard
 * will then use its own default selection logic.
 */
export function findDefaultVariantForPriceRange(
  product: Product,
  minPrice?: number,
  maxPrice?: number
): any {
  if (!product.variants || product.variants.length === 0) {
    return null;
  }

  const min = minPrice ?? 0;
  const max = maxPrice ?? Infinity;

  product.variants.forEach((v: any) => {
    const price = v.finalPrice ?? v.price ?? 0;
    const inRange = price >= min && price <= max;
    const stock = v.inventoryCount ?? 0;
  });

  const inStockInRange = product.variants.filter((variant: any) => {
    const price = variant.finalPrice ?? variant.price ?? 0;
    return price >= min && price <= max && (variant.inventoryCount ?? 0) > 0;
  });

  if (inStockInRange.length === 0) {
    return null;
  }

  const pick = inStockInRange.reduce((best: any, current: any) =>
    (current.finalPrice ?? Infinity) < (best.finalPrice ?? Infinity) ? current : best
  , inStockInRange[0]);

  return pick;
}
